import { HsAmbientLight } from '../../../engine-new/objects/lights/ambient-light.class';
import { HsDirectionalLight } from '../../../engine-new/objects/lights/directional-light.class';
import type { HsLight } from '../../../engine-new/objects/lights/light.class';
import { HsPointLight } from '../../../engine-new/objects/lights/point-light.class';
import { HsSpotLight } from '../../../engine-new/objects/lights/spot-light.class';
import { HsColor } from '../../../math-new/objects/color.class';
import type { HsHypersceneTypes } from '../../../math-new/types/hyperscene-types.type';

/**
 * Handles has dynamic lights.
 */
export namespace HsShaderLightFunctions {
  export function hasDynamicLights(lights: HsLight[]): boolean {
    return lights.some((light: HsLight) => light.dynamic);
  }

  export function makeUniformArrays(lights: HsLight[]): string {
    const { transforms, colors } = countDynamicSlots(lights);
    return makeDynamicTransformUniform(transforms) + makeDynamicColorsUniform(colors);
  }

  /**
   * Writes the current transforms and colors of the dynamic lights into `params` (vec3 each) and `colors` (vec4
   * each), in the slots `makeLight` reads; returns false when the arrays are too short.
   */
  export function writeDynamicValues(lights: HsLight[], params: Float32Array, colors: Float32Array): boolean {
    let transform: number = 0;
    let color: number = 0;
    for (let i: number = 0; i < lights.length; i++) {
      const light: HsLight = lights[i];
      if (!light.dynamic) {
        continue;
      }
      const slots: number = light instanceof HsSpotLight ? 2 : 1;
      if ((transform + slots) * 3 > params.length || (color + 1) * 4 > colors.length) {
        return false;
      }
      if (light instanceof HsDirectionalLight) {
        params.set(light.getDynamicDirection(), transform * 3);
      } else if (light instanceof HsPointLight) {
        params.set(light.getDynamicPosition(), transform * 3);
      } else if (light instanceof HsSpotLight) {
        params.set(light.getDynamicPosition(), transform * 3);
        params.set(light.getDynamicDirection(), (transform + 1) * 3);
      }
      colors[color * 4] = light.color.r;
      colors[color * 4 + 1] = light.color.g;
      colors[color * 4 + 2] = light.color.b;
      colors[color * 4 + 3] = light.intensity;
      transform += slots;
      color++;
    }
    return true;
  }

  /** Number of vec3 transform slots and vec4 color slots the dynamic lights take. */
  export function countDynamicSlots(lights: HsLight[]): { transforms: number; colors: number } {
    let transforms: number = 0;
    let colors: number = 0;
    for (let i: number = 0; i < lights.length; i++) {
      const light: HsLight = lights[i];
      if (light.dynamic) {
        colors++;
        transforms += light instanceof HsSpotLight ? 2 : 1;
      }
    }
    return { transforms, colors };
  }

  export function makeLight(lights: HsLight[], specular: boolean): string {
    let code: string = '';
    let transformOffset: number = 0;
    let colorOffset: number = 0;
    for (let i: number = 0; i < lights.length; i++) {
      const light: HsLight = lights[i];
      code += addLightCode(light, transformOffset, colorOffset, specular);
      if (light.dynamic) {
        transformOffset += light instanceof HsSpotLight ? 2 : 1;
        colorOffset++;
      }
    }
    return code;
  }

  export function makeAmbientLight(lights: HsLight[]): string {
    const ambientLight: HsColor = HsColor.makeBlack();
    for (let i: number = 0; i < lights.length; i++) {
      const light: HsLight = lights[i];
      if (light instanceof HsAmbientLight) {
        ambientLight.add(light.color, light.intensity);
      }
    }
    return makeVec3Code(ambientLight.values);
  }

  function makeDiffuseDirectionalLight(light: HsDirectionalLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const directionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicDirection());
    return `diffuse += addDirectionalLight(${color}, ${directionCode}, normal);\n`;
  }

  function makeDiffusePontLight(light: HsPointLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const positionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicPosition());
    return `diffuse += addPointLight(${color}, ${positionCode}, ${floatToCode(light.radius)},
      ${floatToCode(light.falloff)}, position, normal);\n`;
  }

  function makeDiffuseSpotLight(light: HsSpotLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const positionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicPosition());
    const directionCode: string = makeTransformCode(light.dynamic, dynamicOffset + 1, light.getDynamicDirection());
    return `diffuse += addSpotLight(${color}, ${positionCode}, ${floatToCode(light.angle)},
    position, ${directionCode}, normal);\n`;
  }

  function makeSpecularDirectionalLight(light: HsDirectionalLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const directionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicDirection());
    return `specular += addDirectionalLight(${color}, ${directionCode}, normal, intersectionDirection);\n`;
  }

  function makeSpecularPontLight(light: HsPointLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const positionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicPosition());
    return `specular += addPointLight(${color}, ${positionCode}, ${floatToCode(light.radius)},
      ${floatToCode(light.falloff)}, position, normal, intersectionDirection);\n`;
  }

  function makeSpecularSpotLight(light: HsSpotLight, dynamicOffset: number, colorOffset: number): string {
    const color: string = makeColorCode(light.dynamic, colorOffset, light.color, light.intensity);
    const positionCode: string = makeTransformCode(light.dynamic, dynamicOffset, light.getDynamicPosition());
    const directionCode: string = makeTransformCode(light.dynamic, dynamicOffset + 1, light.getDynamicDirection());
    return `specular += addSpotLight(${color}, ${positionCode}, ${floatToCode(light.angle)},
    position, ${directionCode}, normal, intersectionDirection);\n`;
  }

  function addLightCode(light: HsLight, transformOffset: number, colorOffset: number, specular: boolean): string {
    if (light instanceof HsDirectionalLight) {
      if (specular) {
        return makeSpecularDirectionalLight(light, transformOffset, colorOffset);
      }
      return makeDiffuseDirectionalLight(light, transformOffset, colorOffset);
    }
    if (light instanceof HsPointLight) {
      if (specular) {
        return makeSpecularPontLight(light, transformOffset, colorOffset);
      }
      return makeDiffusePontLight(light, transformOffset, colorOffset);
    }
    if (light instanceof HsSpotLight) {
      if (specular) {
        return makeSpecularSpotLight(light, transformOffset, colorOffset);
      }
      return makeDiffuseSpotLight(light, transformOffset, colorOffset);
    }
    return '';
  }

  function makeTransformCode(isDynamic: boolean, index: number, transform: HsHypersceneTypes.Array3): string {
    if (isDynamic) {
      return `u_lightParams[${index}]`;
    }
    return makeVec3Code(transform);
  }

  function makeColorCode(isDynamic: boolean, index: number, color: HsColor, intensity: number): string {
    if (isDynamic) {
      return `u_lightColors[${index}]`;
    }
    return `vec4(${floatToCode(color.r)}, ${floatToCode(color.g)}, ${floatToCode(color.b)}, ${floatToCode(intensity)})`;
  }

  function makeDynamicTransformUniform(dynamicLightsCount: number): string {
    if (dynamicLightsCount === 0) {
      return '';
    }
    return `uniform vec3 u_lightParams[${dynamicLightsCount}];\n`;
  }

  function makeDynamicColorsUniform(dynamicLightsCount: number): string {
    if (dynamicLightsCount === 0) {
      return '';
    }
    return `uniform vec4 u_lightColors[${dynamicLightsCount}];\n`;
  }

  function floatToCode(value: number): string {
    return value % 1 === 0 ? value.toFixed(1) : value.toString();
  }

  function makeVec3Code(vector: HsHypersceneTypes.Array3): string {
    return `vec3(${floatToCode(vector[0])}, ${floatToCode(vector[1])}, ${floatToCode(vector[2])})`;
  }
}
